/** Dossiers IMAP propriétaires Aurélia (§11.1) — préfixe fixe. */

import { decodeImapMutf7 } from "@/lib/folders/folderLabels";
import type { NavFolderSlot } from "@/lib/mail/folderNavSections";
import type { MailFolder } from "@/lib/types/mail";

/** Parent ASCII (CREATE IMAP fiable). Détection aussi « Aurélia » / sous INBOX. */
export const AURELIA_IMAP_PARENT = "Aurelia";

export const AURELIA_IMAP_SLOT_LEAVES: Partial<Record<NavFolderSlot, string>> = {
  sent: "Envoyés",
  drafts: "Brouillons",
  snoozed: "En attente",
  junk: "Indésirables",
  trash: "Corbeille",
};

/** Nom IMAP décodé (MUTF-7 → UTF-8) pour match / affichage. */
export function decodeAureliaImapName(folderName: string): string {
  return decodeImapMutf7(folderName.trim());
}

export function isAureliaImapNamespace(folderName: string): boolean {
  const name = decodeAureliaImapName(folderName);
  // Racine (Pro/Gmail) ou sous INBOX (OVH Basic, IONOS, SFR, Orange…).
  return /^(?:INBOX[./])?(?:Aurelia|Aur[eé]lia)(?:[./]|$)/i.test(name);
}

function folderLeaf(name: string): string {
  const decoded = decodeAureliaImapName(name);
  if (decoded.includes("/")) {
    const parts = decoded.split("/");
    return parts[parts.length - 1] ?? decoded;
  }
  if (decoded.includes(".")) {
    const parts = decoded.split(".");
    return parts[parts.length - 1] ?? decoded;
  }
  return decoded;
}

function normalizeLeafKey(leaf: string): string {
  return leaf
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[\s._/-]+/g, "_");
}

export function aureliaSlotForFolder(folderName: string): NavFolderSlot | null {
  if (!isAureliaImapNamespace(folderName)) return null;
  const leafKey = normalizeLeafKey(folderLeaf(folderName));
  for (const [slot, leaf] of Object.entries(AURELIA_IMAP_SLOT_LEAVES) as Array<
    [NavFolderSlot, string]
  >) {
    if (normalizeLeafKey(leaf) === leafKey) return slot;
  }
  return null;
}

export function findAureliaFolderForSlot(
  folders: MailFolder[],
  slot: NavFolderSlot
): MailFolder | null {
  const want = AURELIA_IMAP_SLOT_LEAVES[slot];
  if (!want) return null;
  const wantKey = normalizeLeafKey(want);
  for (const folder of folders) {
    if (!isAureliaImapNamespace(folder.name)) continue;
    const fromName = normalizeLeafKey(folderLeaf(folder.name));
    if (fromName === wantKey) return folder;
    // Label humanisé (« Brouillons ») si le leaf MUTF-7 brut ne matche pas encore.
    if (folder.label && normalizeLeafKey(folder.label) === wantKey) {
      return folder;
    }
  }
  return null;
}

/** Chemin lisible pour selects / pastilles (Aurelia/Brouillons, pas seulement « Brouillons »). */
export function formatImapFolderPickerLabel(folder: MailFolder): string {
  const decoded = decodeAureliaImapName(folder.name);
  if (isAureliaImapNamespace(folder.name)) {
    return decoded;
  }
  if (decoded.includes("/") || decoded.includes(".")) {
    return decoded;
  }
  return folder.label || decoded;
}

/** Score bas = meilleur. Les dossiers Aurelia/ gagnent toujours. */
export function aureliaFolderPreferScore(folder: MailFolder): number {
  return isAureliaImapNamespace(folder.name) ? -100 : 0;
}
